import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Landmark, Plus, CalendarRange, Edit3 } from "lucide-react";
import { FounderText } from "@/components/FounderText";
import { HistoryEntry, SectionLinks, ReliabilityBadge, dateText } from "@/components/HistoryEntry";
import { HistoryEntryDialog } from "@/components/HistoryEntryDialog";

const SideNav = ({ sections, current, onSelect }) => {
  const top = sections.filter(s => !s.parent_key);
  const navBtn = (s, sub) => (
    <button key={s.key} onClick={() => onSelect(s.key)} data-testid={`section-nav-${s.key}`}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${sub ? "pl-7" : ""} ${current === s.key ? "bg-[#993D22] text-[#FAF7F2]" : "text-[#3A2312] hover:bg-[#F0E6D8]"}`}>
      {s.title} <span className="opacity-60 text-xs">({s.entry_count})</span>
    </button>
  );
  return (
    <nav className="space-y-1" data-testid="history-nav">
      <button onClick={() => onSelect("__timeline")} data-testid="section-nav-timeline"
        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${current === "__timeline" ? "bg-[#261911] text-[#FAF7F2]" : "text-[#261911] hover:bg-[#F0E6D8] font-semibold"}`}>
        <CalendarRange className="w-4 h-4" /> Frise chronologique
      </button>
      {top.map(s => (
        <React.Fragment key={s.key}>
          {navBtn(s)}
          {sections.filter(c => c.parent_key === s.key).map(c => navBtn(c, true))}
        </React.Fragment>
      ))}
    </nav>
  );
};

const Intro = ({ section, canEdit, onSaved }) => {
  const { API } = useAuth();
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const save = async () => {
    try { await axios.put(`${API}/history/sections/${section.key}`, { intro: draft }); toast.success("Introduction enregistrée"); setEdit(false); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  if (edit) return (
    <div className="mb-6 space-y-2">
      <Textarea rows={5} value={draft} onChange={e => setDraft(e.target.value)} data-testid="section-intro-input" />
      <div className="flex gap-2"><Button size="sm" className="btn-terra" onClick={save} data-testid="save-section-intro">Enregistrer</Button><Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Annuler</Button></div>
    </div>
  );
  return (
    <div className="mb-6">
      {section.intro ? <p className="text-[#3A2312] whitespace-pre-wrap leading-relaxed max-w-3xl" data-testid="section-intro">{section.intro}</p>
        : (section.entry_count ? null : <p className="text-sm italic text-[#6E5B4F]">Cette rubrique attend ses premiers contenus.</p>)}
      {canEdit && <button className="text-xs text-[#993D22] mt-2 inline-flex items-center gap-1" onClick={() => { setDraft(section.intro || ""); setEdit(true); }} data-testid="edit-section-intro"><Edit3 className="w-3 h-3" />Modifier l'introduction</button>}
    </div>
  );
};

const Timeline = ({ entries, sectionsBy }) => (
  <ol className="relative border-l-2 border-[#D9822B] ml-3 space-y-8" data-testid="timeline">
    {entries.length === 0 && <p className="pl-6 text-sm italic text-[#6E5B4F]">Aucun contenu daté pour l'instant. Ajoutez une année à un contenu pour qu'il apparaisse ici.</p>}
    {entries.map(e => (
      <li key={e.id} className="pl-6 relative" data-testid={`timeline-item-${e.id}`}>
        <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-[#FAF7F2] border-2 border-[#993D22]" />
        <div className="font-serif-heritage text-2xl text-[#993D22]">{dateText(e)}</div>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span className="font-semibold text-[#261911]">{e.title}</span>
          <ReliabilityBadge value={e.reliability} />
          <span className="text-[10px] uppercase tracking-wider text-[#6E5B4F]">{sectionsBy[e.section_key]?.title}</span>
        </div>
        {e.body && <p className="text-sm text-[#3A2312] mt-1 line-clamp-3">{e.body}</p>}
      </li>
    ))}
  </ol>
);

export const HistoryPage = () => {
  const { API, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const current = params.get("rubrique") || "origines";
  const [sections, setSections] = useState([]);
  const [entries, setEntries] = useState([]);
  const [dialog, setDialog] = useState({ open: false, entry: null });
  const canEdit = !!(user && (user.is_super_admin || user.role === "admin"));
  const canPropose = !!(user && !canEdit && (user.validated || user.role === "elder"));
  const [proposals, setProposals] = useState([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const sectionsBy = useMemo(() => Object.fromEntries(sections.map(s => [s.key, s])), [sections]);
  const section = sectionsBy[current];

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([
      axios.get(`${API}/history/sections`),
      axios.get(`${API}/history/entries`, { params: current === "__timeline" ? { timeline: true } : { section_key: current } }),
    ]);
    setSections(s.data); setEntries(e.data);
    if (canEdit) axios.get(`${API}/history/proposals`).then(r => setProposals(r.data)).catch(() => {});
  }, [API, current, canEdit]);
  const approve = async (id) => {
    try { await axios.post(`${API}/history/proposals/${id}/approve`); toast.success("Proposition publiée dans la rubrique"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(); }, [load, user]);

  const archive = async (e) => {
    if (!window.confirm(`Archiver « ${e.title} » ? Le contenu sera masqué mais conservé.`)) return;
    try { await axios.post(`${API}/history/entries/${e.id}/archive`); toast.success("Contenu archivé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="history-page">
      <div className="label-uppercase text-[#993D22] mb-2">Patrimoine</div>
      <h1 className="font-serif-heritage text-4xl sm:text-5xl lg:text-6xl text-[#261911] mb-3 flex items-center gap-3">
        <Landmark className="w-10 h-10 text-[#D9822B]" /> Histoire et mémoire
      </h1>
      <p className="text-[#6E5B4F] max-w-3xl mb-8">Chaque contenu indique sa fiabilité : fait documenté, tradition orale ou hypothèse à vérifier. Les dates anciennes sont le plus souvent estimatives.</p>

      <FounderText canEdit={canEdit} />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 self-start"><SideNav sections={sections} current={current} onSelect={k => setParams({ rubrique: k })} /></aside>
        <main>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-serif-heritage text-3xl text-[#261911]" data-testid="section-title">{current === "__timeline" ? "Frise chronologique" : section?.title}</h2>
            {canPropose && current !== "__timeline" && (
              <Button variant="outline" className="border-[#993D22] text-[#993D22]" onClick={() => setProposalOpen(true)} data-testid="propose-entry-btn"><Plus className="w-4 h-4 mr-1" />Proposer un contenu</Button>
            )}
            {canEdit && current !== "__timeline" && (
              <Button className="btn-terra" onClick={() => setDialog({ open: true, entry: null })} data-testid="add-entry-btn"><Plus className="w-4 h-4 mr-1" />Ajouter un contenu</Button>
            )}
          </div>
          {current === "__timeline" ? <Timeline entries={entries} sectionsBy={sectionsBy} /> : section && (
            <>
              <Intro section={section} canEdit={canEdit} onSaved={load} />
              <div className="mb-6"><SectionLinks sectionKey={section.key} clanKey={section.clan_key} /></div>
              {section.key === "cinq_familles" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {sections.filter(s => s.parent_key === "cinq_familles").map(s => (
                    <button key={s.key} onClick={() => setParams({ rubrique: s.key })} className="heritage-card p-4 text-left hover:border-[#993D22]" data-testid={`family-card-${s.key}`}>
                      <div className="font-serif-heritage text-xl text-[#261911]">{s.title}</div>
                      <div className="text-xs text-[#6E5B4F]">{s.entry_count} contenu(s)</div>
                    </button>
                  ))}
                </div>
              )}
              <div className="space-y-4">
                {entries.map(e => <HistoryEntry key={e.id} e={e} canEdit={canEdit} sectionsBy={sectionsBy} onEdit={en => setDialog({ open: true, entry: en })} onArchive={archive} />)}
                {entries.length === 0 && <p className="text-sm italic text-[#6E5B4F]" data-testid="section-empty">Informations à compléter.</p>}
              </div>
            </>
          )}
        </main>
      </div>

      {canEdit && proposals.length > 0 && (
        <section className="mt-12" data-testid="history-proposals">
          <h2 className="font-serif-heritage text-2xl text-[#993D22] mb-3">Propositions des membres à examiner ({proposals.length})</h2>
          <div className="space-y-4">
            {proposals.map(p => (
              <div key={p.id}>
                <HistoryEntry e={p} canEdit sectionsBy={sectionsBy} sectionTitle={sectionsBy[p.section_key]?.title} onEdit={en => setDialog({ open: true, entry: en })} onArchive={archive} />
                <Button size="sm" className="btn-terra mt-2" onClick={() => approve(p.id)} data-testid={`approve-proposal-${p.id}`}>Publier dans la rubrique</Button>
              </div>
            ))}
          </div>
        </section>
      )}
      <HistoryEntryDialog open={proposalOpen} onOpenChange={setProposalOpen} entry={null} proposal
        sectionKey={current === "__timeline" ? "origines" : current} sections={sections} onSaved={load} />
      <HistoryEntryDialog open={dialog.open} onOpenChange={o => setDialog(d => ({ ...d, open: o }))} entry={dialog.entry}
        sectionKey={current === "__timeline" ? "origines" : current} sections={sections} onSaved={load} />
    </div>
  );
};

export default HistoryPage;
